import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Badge } from "./ui/badge";
import { 
  Plus, Trash2, Edit, ArrowUpDown, ArrowUp, ArrowDown, Users, 
  AlertCircle, CheckSquare, Scale, Mail, Phone, Calendar as CalendarIcon,
  Loader2, Check
} from "lucide-react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import Pagination from "./Pagination";
import { useSettings } from "../contexts/SettingsContext";
import { formatTime } from "../utils/formatters";
import AddTeamMemberModal from "./AddTeamMemberModal";
import TabPageLayout from './TabPageLayout';
import CardListViewToggle from './CardListViewToggle';
import MassActionToolbar from './MassActionToolbar';
import EmptyState from './EmptyState';
import { PrimaryButton, SecondaryButton, DangerButton } from './Buttons';
import { useRoles } from '../hooks/useData';
import RoleSelector from './RoleSelector';
import StandardModal from './StandardModal';
import InfoCard from './InfoCard';
import ScheduleEditor from './ScheduleEditor';
import { formatShortDate } from '../utils/dateFormatters';
import QuickAvailabilityModal from './QuickAvailabilityModal';
import TeamScheduleModal from './TeamScheduleModal';
import ResourceCard from './ResourceCard';

const DAYS = [
  { value: "monday", label: "Monday" },
  { value: "tuesday", label: "Tuesday" },
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "friday", label: "Friday" },
  { value: "saturday", label: "Saturday" },
  { value: "sunday", label: "Sunday" }
];

const TeamMembersTab = ({ teamMembers, onRefresh, isLoading: propsLoading, isError: propsError }) => {
  const navigate = useNavigate();
  const { timeFormat } = useSettings();
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState("card");
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, id: null, name: "" });
  const [editingTeamMember, setEditingTeamMember] = useState(null);

  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [expandedTeamMembers, setExpandedTeamMembers] = useState({});
  const itemsPerPage = 12;
  
  // Mass action states
  const [massActionMode, setMassActionMode] = useState(false);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState([]);
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  const [quickAvailabilityOpen, setQuickAvailabilityOpen] = useState(false);
  const [selectedTeamForSchedule, setSelectedTeamForSchedule] = useState(null);
  
  // Loading states
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  
  // Role management with useSWR
  const { roles } = useRoles();
  const [selectedRoleId, setSelectedRoleId] = useState("");

  // Form states
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [recurringAvailability, setRecurringAvailability] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [usernameManuallyEdited, setUsernameManuallyEdited] = useState(false);
  const [schedulerWeight, setSchedulerWeight] = useState(3);
  const [formHasChanges, setFormHasChanges] = useState(false);

  // Fetch pending requests
  useEffect(() => {
    const fetchPendingRequests = async () => {
      try {
        const response = await api.get('/change-requests/pending');
        setPendingRequests(response.data || []);
      } catch (error) {
        console.error("Error fetching pending requests:", error);
      }
    };

    fetchPendingRequests();
    const interval = setInterval(fetchPendingRequests, 30000);
    return () => clearInterval(interval);
  }, []);

  const hasPendingRequest = (teamMemberId) => {
    return pendingRequests.some(req => req.teamMemberId === teamMemberId && req.status === 'pending');
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 inline" />;
    return sortDirection === "asc" ? 
      <ArrowUp className="w-3 h-3 ml-1 inline" /> : 
      <ArrowDown className="w-3 h-3 ml-1 inline" />;
  };
  
  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    setSelectedTeamMembers([]);
  };
  
  const toggleTeamMemberSelection = (teamMemberId) => {
    setSelectedTeamMembers(prev => 
      prev.includes(teamMemberId) 
        ? prev.filter(id => id !== teamMemberId)
        : [...prev, teamMemberId]
    );
  };
  
  const selectAllTeamMembers = () => {
    setSelectedTeamMembers(paginatedTeamMembers.map(tm => tm.id));
  };
  
  const deselectAllTeamMembers = () => {
    setSelectedTeamMembers([]);
  };
  
  const handleBulkDelete = async () => {
    setIsBulkDeleting(true);
    try {
      const deletePromises = selectedTeamMembers.map(teamMemberId =>
        api.delete(`/team/${teamMemberId}`)
      );
      
      await Promise.all(deletePromises);
      toast.success(`${selectedTeamMembers.length} team member(s) deleted successfully`);
      setSelectedTeamMembers([]);
      setMassActionMode(false);
      setBulkDeleteConfirm(false);
      onRefresh();
    } catch (error) {
      console.error("Error deleting team members:", error);
      toast.error("Failed to delete some team members");
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const handleNameChange = (value) => {
    setName(value);
    if (!usernameManuallyEdited && !editingTeamMember) {
      const generatedUsername = value
        .toLowerCase()
        .replace(/[^a-z0-9]/g, '')
        .slice(0, 20);
      setUsername(generatedUsername);
    }
  };

  const handleEditClick = (teamMember) => {
    setEditingTeamMember(teamMember);
    setName(teamMember.name);
    setEmail(teamMember.email || "");
    setPhone(teamMember.phone || "");
    setUsername(teamMember.username || "");
    setPassword("");
    setRecurringAvailability(teamMember.recurringAvailability || []);
    setUnavailableDates(teamMember.unavailableDates || []);
    setSchedulerWeight(teamMember.schedulerWeight || 3);
    setSelectedRoleId(teamMember.roleId || "");
    setOpen(true);
  };

  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error("Please enter team member name");
      return;
    }

    if (!editingTeamMember && !selectedRoleId) {
      toast.error("Please select a role for this team member");
      return;
    }

    if (!editingTeamMember && (!username || !password)) {
      toast.error("Please enter username and password for new team member");
      return;
    }

    setIsSaving(true);
    try {
      const selectedRole = selectedRoleId ? roles.find(r => r.id === selectedRoleId) : null;
      
      const payload = {
        name: name.trim(),
        email: email?.trim() || null,
        phone: phone?.trim() || null,
        username: editingTeamMember ? editingTeamMember.username : username?.trim(),
        roleId: selectedRoleId || null,
        roleName: selectedRole?.name || null,
        recurringAvailability: recurringAvailability || [],
        unavailableDates: unavailableDates || [],
        payRate: 20.0,
        schedulerWeight: schedulerWeight || 3,
      };

      if (password?.trim()) {
        payload.password = password.trim();
      } else if (!editingTeamMember) {
        toast.error("Password is required for new team members");
        setIsSaving(false);
        return;
      }

      if (editingTeamMember) {
        await api.put(`/team/${editingTeamMember.id}`, payload);
        toast.success("Team member updated successfully");
      } else {
        await api.post('/team', payload);
        toast.success("Team member added successfully");
      }
      
      setFormHasChanges(false);
      setOpen(false);
      resetForm();
      onRefresh();
    } catch (error) {
      console.error("Error saving team member:", error);
      let errorMessage = "Failed to save team member";
      
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          const validationErrors = error.response.data.detail.map(err => 
            `${err.loc?.[err.loc.length - 1] || 'Field'}: ${err.msg}`
          ).join(', ');
          errorMessage = `Validation errors: ${validationErrors}`;
        }
      }
      
      toast.error(errorMessage, { duration: 6000 });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteClick = (teamMember) => {
    setDeleteConfirm({ open: true, id: teamMember.id, name: teamMember.name });
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await api.delete(`/team/${deleteConfirm.id}`);
      toast.success("Team member deleted successfully");
      setDeleteConfirm({ open: false, id: null, name: "" });
      onRefresh();
    } catch (error) {
      console.error("Error deleting team member:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete team member";
      
      if (error.response?.status === 400) {
        toast.error(errorMsg, { duration: 6000 });
      } else {
        toast.error("Failed to delete team member");
      }
      
      setDeleteConfirm({ open: false, id: null, name: "" });
    } finally {
      setIsDeleting(false);
    }
  };

  const resetForm = () => {
    setEditingTeamMember(null);
    setName("");
    setEmail("");
    setPhone("");
    setUsername("");
    setPassword("");
    setRecurringAvailability([]);
    setUnavailableDates([]);
    setUsernameManuallyEdited(false);
    setSchedulerWeight(3);
    setSelectedRoleId("");
    setFormHasChanges(false);
  };

  const handleOpenChange = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  // Sort team members
  const sortedTeamMembers = [...(teamMembers || [])].sort((a, b) => {
    let compareValue = 0;
    
    switch (sortField) {
      case "name":
        compareValue = a.name.localeCompare(b.name);
        break;
      case "roleName":
        compareValue = (a.roleName || '').localeCompare(b.roleName || '');
        break;
      case "schedulerWeight":
        compareValue = (a.schedulerWeight || 3) - (b.schedulerWeight || 3);
        break;
      default:
        compareValue = 0;
    }
    
    return sortDirection === "asc" ? compareValue : -compareValue;
  });

  // Pagination
  const totalPages = Math.ceil(sortedTeamMembers.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTeamMembers = sortedTeamMembers.slice(startIndex, startIndex + itemsPerPage);

  // Error state
  if (propsError) {
    return (
      <TabPageLayout
        title="Team"
        subtitle="Manage your team members"
      >
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-500" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-1">Failed to load team members</h3>
          <p className="text-sm text-gray-500 mb-4">Please check your connection and try again.</p>
          <PrimaryButton onClick={() => onRefresh()}>
            Try Again
          </PrimaryButton>
        </div>
      </TabPageLayout>
    );
  }

  return (
    <TabPageLayout
      title="Team"
      subtitle="Manage your team members"
      loading={propsLoading}
      actions={
        <div className="flex items-center gap-2 w-full">
          <SecondaryButton
            onClick={toggleMassActionMode}
            className={`h-9 w-9 p-0 flex items-center justify-center ${
              massActionMode ? 'bg-blue-50 border-blue-500 text-blue-600' : ''
            }`}
          >
            <CheckSquare className="w-4 h-4" />
          </SecondaryButton>
          
          <CardListViewToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          
          <PrimaryButton 
            data-testid="add-team-member-button" 
            className="h-9 flex-1 sm:flex-none sm:px-4"
            onClick={() => setOpen(true)}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add
          </PrimaryButton>
        </div>
      }
    >
      <div data-testid="team-members-tab" className="space-y-6">
        <AddTeamMemberModal 
          isOpen={open && !editingTeamMember} 
          onClose={() => setOpen(false)} 
          onSuccess={onRefresh} 
        />
          
        {/* Edit Team Member Modal */}
        {editingTeamMember && (
          <StandardModal
            isOpen={open}
            onClose={handleOpenChange}
            title={`Edit ${editingTeamMember.name}`}
            description="Update team member details"
            size="md"
            footer={
              <>
                <SecondaryButton onClick={() => handleOpenChange(false)} className="flex-1 sm:flex-none" disabled={isSaving}>
                  Cancel
                </SecondaryButton>
                <PrimaryButton onClick={handleSubmit} className="flex-1 sm:flex-none" disabled={isSaving}>
                  {isSaving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </PrimaryButton>
              </>
            }
          >
            <Tabs defaultValue="basic" className="w-full">
              <div className="mb-4">
                <TabsList className="grid w-full grid-cols-3 h-auto sm:h-11">
                  <TabsTrigger value="basic" className="text-xs sm:text-sm py-2">Basic</TabsTrigger>
                  <TabsTrigger value="role" className="text-xs sm:text-sm py-2">Role</TabsTrigger>
                  <TabsTrigger value="availability" className="text-xs sm:text-sm py-2">Schedule</TabsTrigger>
                </TabsList>
              </div>
              
              {/* Tab 1: Basic Info */}
              <TabsContent value="basic" className="space-y-4 mt-6">
                <div>
                  <Label htmlFor="team-member-name">Name <span className="text-red-500">*</span></Label>
                  <Input
                    id="team-member-name"
                    value={name}
                    onChange={(e) => handleNameChange(e.target.value)}
                    placeholder="Team member name"
                    className={`mt-1 ${!name.trim() && name !== '' ? 'border-red-300 focus:border-red-500' : ''}`}
                  />
                  {!name.trim() && name !== '' && (
                    <p className="text-sm text-red-500 mt-1">Name is required</p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="team-member-email">Email</Label>
                    <Input
                      id="team-member-email"
                      type="email"
                      value={email}
                      disabled={true}
                      placeholder="email@example.com"
                      className="mt-1 bg-gray-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Can't edit. Delete & recreate to change.</p>
                    {email && !editingTeamMember?.emailVerified && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/team/${editingTeamMember.id}/resend-verification`);
                            toast.success('Verification email sent!');
                          } catch (error) {
                            toast.error('Failed to send email');
                          }
                        }}
                        className="text-xs text-blue-600 hover:underline font-medium mt-1"
                      >
                        Resend Verification Email
                      </button>
                    )}
                  </div>
                  <div>
                    <Label htmlFor="team-member-phone">Phone</Label>
                    <Input
                      id="team-member-phone"
                      type="tel"
                      value={phone}
                      disabled={true}
                      placeholder="(555) 123-4567"
                      className="mt-1 bg-gray-100 cursor-not-allowed"
                    />
                    <p className="text-xs text-gray-500 mt-1">Can't edit. Delete & recreate to change.</p>
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="team-member-username">
                      Username 
                      <span className="text-xs text-gray-500 ml-1">(cannot change)</span>
                    </Label>
                    <Input
                      id="team-member-username"
                      value={username}
                      disabled={true}
                      className="bg-gray-100 mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="team-member-password">
                      Password 
                      <span className="text-xs text-gray-500 ml-1">(leave blank to keep)</span>
                    </Label>
                    <Input
                      id="team-member-password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="New password"
                      className="mt-1"
                    />
                  </div>
                </div>
              </TabsContent>

              {/* Tab 2: Role & Priority */}
              <TabsContent value="role" className="space-y-4 mt-6">
                <div>
                  <Label>Role <span className="text-red-500">*</span></Label>
                  <RoleSelector
                    roles={roles}
                    value={selectedRoleId}
                    onChange={setSelectedRoleId}
                    placeholder="Select a role..."
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label>Priority</Label>
                  <Select 
                    value={String(schedulerWeight || 3)} 
                    onValueChange={(value) => setSchedulerWeight(parseInt(value))}
                  >
                    <SelectTrigger className="w-full mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="5">High</SelectItem>
                      <SelectItem value="3">Normal</SelectItem>
                      <SelectItem value="1">Low</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500 mt-1">
                    Higher priority gets more trips from auto-scheduler
                  </p>
                </div>
              </TabsContent>

              {/* Tab 3: Schedule */}
              <TabsContent value="availability" className="mt-6">
                <ScheduleEditor
                  value={recurringAvailability}
                  onChange={(newSchedule) => {
                    setRecurringAvailability(newSchedule);
                    setFormHasChanges(true);
                  }}
                />
              </TabsContent>
            </Tabs>
          </StandardModal>
        )}
        
        {/* Mass Action Toolbar */}
        {massActionMode && (
          <MassActionToolbar
            selectedCount={selectedTeamMembers.length}
            onSelectAll={selectAllTeamMembers}
            onDeselectAll={deselectAllTeamMembers}
            onDelete={() => setBulkDeleteConfirm(true)}
            itemName="team member"
          />
        )}

        {/* Content */}
        {!teamMembers || teamMembers.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No Team Members Added Yet"
            description="Add your first team member to get started"
            actionLabel="Add Team Member"
            onAction={() => setOpen(true)}
          />
        ) : viewMode === "card" ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedTeamMembers.map((teamMember) => {
              const isSelected = selectedTeamMembers.includes(teamMember.id);
              const roleColor = roles.find(r => r.id === teamMember.roleId)?.color || '#3B82F6';
              
              const expandedContent = (
                <>
                  {!teamMember.emailVerified && teamMember.email && (
                    <div className="mb-3">
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                        <p className="text-xs font-semibold text-yellow-800 mb-2">⚠️ Email Not Verified</p>
                        <button
                          onClick={async () => {
                            try {
                              await api.post(`/team/${teamMember.id}/resend-verification`);
                              toast.success('Verification email sent!');
                            } catch (error) {
                              toast.error('Failed to send email');
                            }
                          }}
                          className="text-xs text-blue-600 hover:underline font-medium"
                        >
                          Resend Verification Email →
                        </button>
                      </div>
                    </div>
                  )}

                  <div>
                    <p className="text-sm font-semibold text-gray-700 mb-2">Priority</p>
                    <Badge 
                      className={`text-xs ${
                        teamMember.schedulerWeight >= 4 ? 'bg-green-100 text-green-800' :
                        teamMember.schedulerWeight === 3 ? 'bg-gray-100 text-gray-800' :
                        'bg-orange-100 text-orange-800'
                      }`}
                    >
                      <Check className="w-3 h-3 mr-1" />
                      {teamMember.schedulerWeight >= 4 ? 'High' :
                       teamMember.schedulerWeight === 3 ? 'Normal' : 'Low'} Priority
                    </Badge>
                  </div>
                  
                  {teamMember.recurringAvailability?.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-gray-700 mb-2">Schedule</p>
                      <div className="space-y-1">
                        {teamMember.recurringAvailability.map((avail, idx) => (
                          <div key={idx} className="flex justify-between text-xs text-gray-600 bg-gray-50 p-2 rounded">
                            <span className="font-medium">{DAYS.find(d => d.value === avail.day)?.label}</span>
                            <span>{formatTime(avail.startTime, timeFormat)} - {formatTime(avail.endTime, timeFormat)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {teamMember.unavailableDates?.length > 0 && (
                    <div>
                      <p className="text-sm font-semibold text-red-700 mb-2">Unavailable Dates</p>
                      <div className="flex flex-wrap gap-1">
                        {teamMember.unavailableDates.slice(0, 6).map(date => (
                          <Badge key={date} className="bg-red-100 text-red-800 text-xs">
                            {formatShortDate(date)}
                          </Badge>
                        ))}
                        {teamMember.unavailableDates.length > 6 && (
                          <span className="text-xs text-red-600">+{teamMember.unavailableDates.length - 6} more</span>
                        )}
                      </div>
                    </div>
                  )}
                </>
              );
              
              return (
                <ResourceCard
                  key={teamMember.id}
                  id={teamMember.id}
                  name={teamMember.name}
                  icon={Users}
                  iconColor={roleColor}
                  primaryBadge={
                    teamMember.email && !teamMember.emailVerified ? {
                      label: 'Unverified',
                      color: '#6B7280',
                      variant: 'outline',
                      className: 'text-xs mb-2'
                    } : teamMember.roleName ? {
                      label: teamMember.roleName,
                      color: roleColor,
                      variant: 'outline',
                      className: 'text-xs mb-2'
                    } : null
                  }
                  details={[
                    ...(teamMember.email ? [{ icon: <Mail className="w-3 h-3" />, text: teamMember.email }] : []),
                    ...(teamMember.phone ? [{ icon: <Phone className="w-3 h-3" />, text: teamMember.phone }] : [])
                  ]}
                  additionalBadges={hasPendingRequest(teamMember.id) && (
                    <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 flex items-center gap-1 text-xs mt-2 w-fit">
                      <AlertCircle className="w-3 h-3" />
                      Pending Request
                    </Badge>
                  )}
                  showSeeMore={true}
                  expandedContent={expandedContent}
                  isExpanded={expandedTeamMembers[teamMember.id]}
                  onToggleExpand={() => {
                    setExpandedTeamMembers(prev => ({
                      ...prev,
                      [teamMember.id]: !prev[teamMember.id]
                    }));
                  }}
                  leftBorderColors={[roleColor]}
                  massActionMode={massActionMode}
                  isSelected={isSelected}
                  onToggleSelect={() => toggleTeamMemberSelection(teamMember.id)}
                  additionalClassNames={hasPendingRequest(teamMember.id) ? 'ring-2 ring-orange-400' : ''}
                  onEdit={() => handleEditClick(teamMember)}
                  onDelete={() => handleDeleteClick(teamMember)}
                />
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-[0_8px_24px_rgba(0,0,0,0.04)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[768px]">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    {massActionMode && (
                      <th className="w-12 px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedTeamMembers.length === paginatedTeamMembers.length && paginatedTeamMembers.length > 0}
                          onChange={(e) => e.target.checked ? selectAllTeamMembers() : deselectAllTeamMembers()}
                          className="w-4 h-4 cursor-pointer rounded border-gray-300"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button onClick={() => handleSort("name")} className="flex items-center hover:text-gray-700">
                        Name {getSortIcon("name")}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button onClick={() => handleSort("roleName")} className="flex items-center hover:text-gray-700">
                        <Users className="w-3 h-3 mr-1" /> Role {getSortIcon("roleName")}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button onClick={() => setQuickAvailabilityOpen(true)} className="flex items-center hover:text-blue-600">
                        <CalendarIcon className="w-3 h-3 mr-1" /> Availability
                      </button>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <button onClick={() => handleSort("schedulerWeight")} className="flex items-center hover:text-gray-700">
                        <Scale className="w-3 h-3 mr-1" /> Priority {getSortIcon("schedulerWeight")}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedTeamMembers.map((teamMember) => {
                    const isSelected = selectedTeamMembers.includes(teamMember.id);
                    return (
                      <tr key={teamMember.id} className={`${hasPendingRequest(teamMember.id) ? 'bg-orange-50' : ''} ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'} transition-colors`}>
                        {massActionMode && (
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleTeamMemberSelection(teamMember.id)}
                              className="w-4 h-4 cursor-pointer rounded border-gray-300"
                            />
                          </td>
                        )}
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-gray-900">{teamMember.name}</span>
                                {hasPendingRequest(teamMember.id) && (
                                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-300 text-xs">
                                    <AlertCircle className="w-2.5 h-2.5 mr-1" />
                                    Pending
                                  </Badge>
                                )}
                              </div>
                              {(teamMember.email || teamMember.phone) && (
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {teamMember.phone && <div>{teamMember.phone}</div>}
                                  {teamMember.email && <div className="truncate max-w-[150px]">{teamMember.email}</div>}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        
                        <td className="px-4 py-3">
                          {teamMember.roleName ? (
                            <Badge 
                              className="text-xs text-white"
                              style={{ backgroundColor: roles.find(r => r.id === teamMember.roleId)?.color || '#3B82F6' }}
                            >
                              {teamMember.roleName}
                            </Badge>
                          ) : (
                            <span className="text-xs text-gray-400 italic">No role</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <SecondaryButton
                            size="sm"
                            onClick={() => setSelectedTeamForSchedule(teamMember)}
                            className="text-xs h-8"
                          >
                            View Schedule
                          </SecondaryButton>
                        </td>
                        <td className="px-4 py-3">
                          <Badge 
                            variant="outline"
                            className={`text-xs ${
                              teamMember.schedulerWeight >= 4 ? 'border-green-500 text-green-600' :
                              teamMember.schedulerWeight === 3 ? 'border-gray-400 text-gray-600' :
                              'border-orange-500 text-orange-600'
                            }`}
                          >
                            <Check className="w-3 h-3 mr-1" />
                            {teamMember.schedulerWeight >= 4 ? 'High' :
                             teamMember.schedulerWeight === 3 ? 'Normal' : 'Low'}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditClick(teamMember)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(teamMember)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Delete
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {teamMembers && sortedTeamMembers.length > itemsPerPage && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            itemsPerPage={itemsPerPage}
            totalItems={sortedTeamMembers.length}
          />
        )}

        {/* Delete Team Member Modal */}
        <StandardModal
          isOpen={deleteConfirm.open}
          onClose={() => setDeleteConfirm({ open: false, id: null, name: "" })}
          title="Delete Team Member?"
          description={`Are you sure you want to delete ${deleteConfirm.name}?`}
          size="sm"
          footer={
            <>
              <SecondaryButton
                onClick={() => setDeleteConfirm({ open: false, id: null, name: "" })}
                className="flex-1"
                disabled={isDeleting}
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleDeleteConfirm}
                className="flex-1"
                disabled={isDeleting}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </DangerButton>
            </>
          }
        >
          <InfoCard variant="danger" showIcon={true}>
            <p className="font-medium mb-1">This action cannot be undone.</p>
            <p>This team member will be unassigned from all future trips.</p>
          </InfoCard>
        </StandardModal>
        
        {/* Bulk Delete Modal */}
        <StandardModal
          isOpen={bulkDeleteConfirm}
          onClose={() => setBulkDeleteConfirm(false)}
          title="Delete Multiple Team Members?"
          description={`Are you sure you want to delete ${selectedTeamMembers.length} team member(s)?`}
          size="sm"
          footer={
            <>
              <SecondaryButton
                onClick={() => setBulkDeleteConfirm(false)}
                className="flex-1"
                disabled={isBulkDeleting}
              >
                Cancel
              </SecondaryButton>
              <DangerButton
                onClick={handleBulkDelete}
                className="flex-1"
                disabled={isBulkDeleting}
              >
                {isBulkDeleting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  `Delete ${selectedTeamMembers.length} Members`
                )}
              </DangerButton>
            </>
          }
        >
          <InfoCard variant="danger" showIcon={true}>
            <p className="font-medium mb-1">This action cannot be undone.</p>
            <p>All selected team members will be unassigned from future trips.</p>
          </InfoCard>
        </StandardModal>
        
        {/* Quick Availability Check Modal */}
        <QuickAvailabilityModal
          isOpen={quickAvailabilityOpen}
          onClose={() => setQuickAvailabilityOpen(false)}
          teamMembers={teamMembers}
          roles={roles}
        />
        
        {/* Individual Team Schedule Modal */}
        <TeamScheduleModal
          isOpen={!!selectedTeamForSchedule}
          onClose={() => setSelectedTeamForSchedule(null)}
          teamMember={selectedTeamForSchedule}
          timeFormat={timeFormat}
        />
      </div>
    </TabPageLayout>
  );
};

export default TeamMembersTab;
